import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { Add, Refresh, Language } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { useTenant } from '../../contexts/TenantContext';
import { getAuthToken } from '../../lib/authToken';

const authHeaders = () => ({
  Authorization: `Bearer ${getAuthToken()}`,
  'Content-Type': 'application/json',
});

const CmsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { tenant } = useTenant();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    title: '',
    excerpt: '',
    body: '',
    status: 'draft',
    slug: '',
  });

  const { data: syncStatus } = useQuery({
    queryKey: ['cms-sync'],
    queryFn: async () => {
      const res = await fetch('/api/cms/sync-status', { headers: authHeaders() });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to load sync status');
      return json.data;
    },
  });

  const { data: postsData, isLoading } = useQuery({
    queryKey: ['cms-posts'],
    queryFn: async () => {
      const res = await fetch('/api/cms/posts', { headers: authHeaders() });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to load posts');
      return json.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/cms/posts', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(form),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to create post');
      return json;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cms-posts'] });
      queryClient.invalidateQueries({ queryKey: ['cms-sync'] });
      setDialogOpen(false);
      setForm({ title: '', excerpt: '', body: '', status: 'draft', slug: '' });
      toast.success('Post created');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const publishMutation = useMutation({
    mutationFn: async (post: any) => {
      const res = await fetch(`/api/cms/posts/${post.id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ status: post.status === 'published' ? 'draft' : 'published' }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to update');
      return json;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cms-posts'] });
      queryClient.invalidateQueries({ queryKey: ['cms-sync'] });
      toast.success('Post updated');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const subdomain = tenant?.subdomain || '{subdomain}';

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">Website CMS</Typography>
          <Typography variant="body2" color="text.secondary">
            Publish staff bios (from Team profiles) and blog posts for your public site.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)}>
          New post
        </Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Published</Typography>
              <Typography variant="h4">{syncStatus?.published ?? '—'}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Drafts</Typography>
              <Typography variant="h4">{syncStatus?.drafts ?? '—'}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Language fontSize="small" /> Public API
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontFamily: 'monospace', fontSize: 12 }}>
                GET /api/public/{subdomain}/blog
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                GET /api/public/{subdomain}/staff
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                POST /api/public/{subdomain}/intake
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Alert severity="info" sx={{ mb: 2 }}>
        Staff bios are pulled from Team member profiles (`bio` field). Edit bios under Profile / Team Management.
        Blog posts below are managed here and exposed publicly when status is Published.
      </Alert>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Blog posts</Typography>
            <Button
              size="small"
              startIcon={<Refresh />}
              onClick={() => {
                queryClient.invalidateQueries({ queryKey: ['cms-posts'] });
                queryClient.invalidateQueries({ queryKey: ['cms-sync'] });
              }}
            >
              Refresh
            </Button>
          </Box>
          {isLoading ? (
            <CircularProgress />
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Slug</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Published</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(postsData?.posts || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography color="text.secondary">No posts yet.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  (postsData?.posts || []).map((post: any) => (
                    <TableRow key={post.id}>
                      <TableCell>{post.title}</TableCell>
                      <TableCell>
                        <Typography variant="caption" fontFamily="monospace">{post.slug}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={post.status}
                          color={post.status === 'published' ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {post.publishedAt
                          ? format(new Date(post.publishedAt), 'MMM d, yyyy')
                          : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => publishMutation.mutate(post)}>
                          {post.status === 'published' ? 'Unpublish' : 'Publish'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>New blog post</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Slug (optional)"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                helperText="Auto-generated from title if blank"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="published">Published</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Excerpt"
                value={form.excerpt}
                onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={8}
                label="Body"
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!form.title || !form.body || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {createMutation.isPending ? <CircularProgress size={22} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CmsPage;
